import { DateTime } from 'luxon';
// CORE
import React from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { transactionsUtils } from 'app-components/flux/transactions';

import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { getSmartDateLabel } from 'app-components/utils/date';
import { numberUtils } from 'app-components/utils/number';
// DATA
import { useSelector } from 'react-redux';

import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
// CUSTOM COMPONENTS
import { TxnActionsMenuButton } from 'components/TxnActionHelpers/TxnActionsMenu';

const useStyles = makeStyles<{ past?: true; isIncome: boolean; positive: boolean }>()(
  (theme, { past, isIncome, positive }) => ({
    root: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: past ? '16px' : '16px',
      boxShadow: past ? '' : '0px 0px 4px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.1)',
      backgroundColor: past ? '' : theme.palette.background.paper,
      border: past ? '' : `1px solid ${theme.palette.border.light}`,
      borderBottom: past && `1px solid ${theme.palette.border.light}`,
      height: 78,
      overflow: 'hidden',
      borderRadius: past ? '' : 8,
      '&:hover': {
        borderRadius: 8,
        boxShadow: past ? '' : '0px 0px 2px rgba(51, 51, 51, 0.48), 0px 4px 10px rgba(51, 51, 51, 0.16)',
        background: past ? 'theme.palette.action.hover' : theme.palette.background.paper,
        borderBottom: past ? `1px solid ${theme.palette.border.light}` : `0px solid ${theme.palette.border.light}`,
      },
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flexGrow: 2,
      overflow: 'hidden',
    },
    rightColumn: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-end',
      flexGrow: 0,
      flexShrink: 0,
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: 8,
      fontWeight: 500,
      flexGrow: 0,
      display: past ? 'none' : '',
      marginRight: 16,
      color: theme.palette.common.white,
      backgroundColor: past
        ? (isIncome && theme.palette.legendDim.income) || theme.palette.legendDim.bill
        : (isIncome && theme.palette.legend.income) || theme.palette.legend.bill,
    },
    amount: {
      color: `${positive ? theme.palette.amount.positive : theme.palette.text.primary} !important`,
      width: '100%',
    },
    status: {
      borderRadius: 12,
      padding: '0 12px',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.warning.light,
      height: 24,
      lineHeight: '24px',
    },
  }),
);

interface ViewRowProps {
  past?: true;
  txn: TransactionRecord;
  isIncome: boolean;
  baseId?: string;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                   Row Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ViewRow({ past, txn, isIncome, baseId }: ViewRowProps) {
  const { payee, amount, id, accountId } = txn;

  const positive = Number(amount) > 0;
  const { classes } = useStyles({ isIncome, positive, past });

  const accountsById = useSelector(accountsSelectors.getAccountsById);
  const account = accountsById && accountsById.get(accountId || '');

  const today = DateTime.local().startOf('day');
  const txnDate = transactionsUtils.dateTimeFromTxn(txn);
  const dayDiff = txnDate.diff(today, 'days').days;
  const dayString = getSmartDateLabel(txn.postedOn || txn.stDueOn || '');

  // render return
  return (
    <div id={`${baseId}-row`} className={classes.root} key={id}>
      <Avatar className={classes.avatar}>{payee ? payee[0].toUpperCase() : '!'}</Avatar>
      <div className={classes.leftColumn}>
        <Typography id={`${baseId}-amount`} variant='bodyMedium' noWrap className={classes.amount}>
          {numberUtils.formatNumber(amount, 'USD', '0,0.00', numberUtils.ShowSignEnum.POSITIVE_ONLY)}
        </Typography>
        <Typography id={`${baseId}-account-name`} variant='paragraphRegular' color={'textSecondary'}>
          {account ? account.name : 'Somewhere'}
        </Typography>
      </div>
      <div className={classes.rightColumn}>
        <Typography id={`${baseId}-month-day`} variant='paragraphRegular'>
          {dayString}
        </Typography>

        {txn.source === 'SCHEDULED_TRANSACTION_PENDING' && dayDiff < 0 && (
          <Typography variant='captionRegular' className={classes.status}>
            Past
          </Typography>
        )}
      </div>
      {!past && <TxnActionsMenuButton txn={txn} context={'view-row'} />}
    </div>
  );
}

export default ViewRow;
